import { useEffect } from 'react';
import slugify from 'slugify';
import { Story } from '../../schema';

/**
 * Hook to handle hash-based scroll-to-anchor navigation for stories.
 * Web-specific: Uses browser's window, location, and DOM APIs.
 * For React Native, this would need a platform-specific implementation.
 *
 * @param stories - Array of stories to match against URL hash
 */
export const useScrollToAnchor = (stories?: Story[]): void => {
  useEffect(() => {
    const handleScrollToAnchor = () => {
      const hash = window.location.hash;
      if (!hash || !stories) {
        return;
      }

      // Remove the # from the hash
      const anchorId = hash.substring(1);

      // Find the story that matches this anchor
      const matchingStory = stories.find((story) => {
        const storyAnchor = slugify(story.title, {
          lower: true,
          strict: true,
        }).slice(0, 50);
        return storyAnchor === anchorId;
      });

      if (matchingStory) {
        // Wait a bit for the DOM to be fully rendered
        const scrollToElement = () => {
          const element = document.getElementById(anchorId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          } else {
            // If element not found, try again after a short delay
            setTimeout(scrollToElement, 100);
          }
        };

        // Small delay to ensure stories are rendered
        setTimeout(scrollToElement, 200);
      }
    };

    // Only run if we have data and stories
    if (stories && stories.length > 0) {
      handleScrollToAnchor();
    }

    // Listen for hash changes (when user clicks on story titles)
    const handleHashChange = () => {
      if (stories && stories.length > 0) {
        handleScrollToAnchor();
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [stories]);
};
